/**
 * Where the SPA itself is mounted. Vite's `base` (`DOC_SPA_BASE_PATH`, default `/preview/`)
 * is exposed as `import.meta.env.BASE_URL`; the router mounts at that path minus the trailing
 * slash, so `/` becomes `""` (site root) and `/editor/preview/` becomes `/editor/preview`.
 */

/** Removes trailing slashes; `"/"` becomes `""` (root). */
export function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

/** Router mount path derived from a Vite `BASE_URL`. */
export function routerBasePath(baseUrl: string | undefined): string {
  const trimmed = (baseUrl ?? "/").trim();
  if (!trimmed || trimmed === "./") {
    return "";
  }
  const stripped = stripTrailingSlash(trimmed);
  if (!stripped) {
    return "";
  }
  return stripped.startsWith("/") ? stripped : `/${stripped}`;
}

/** The mount path of this build, e.g. `""`, `"/preview"` or `"/editor/preview"`. */
export const APP_BASE_PATH = routerBasePath(import.meta.env.BASE_URL);

/**
 * Absolute in-app path for a page of a docset, e.g. `docsetPath("public", "guide/intro")`
 * gives `/preview/public/guide/intro` in the default build and `/public/guide/intro` when
 * the SPA is built for the site root.
 */
export function docsetPath(docsetId: string, pagePath = "", basePath = APP_BASE_PATH): string {
  const docset = docsetId.replace(/^\/+|\/+$/g, "");
  const page = pagePath.replace(/^\/+/, "");
  return `${basePath}/${docset}/${page}`.replace(/\/{2,}/g, "/");
}
